/**
 * Modal System - Generic modal dialogue system
 * Uses native <dialog> element with showModal()/close() for consistent, accessible modals.
 * 
 * Usage:
 *   ModalSystem.show({ title: 'Hello', message: 'World' });
 *   ModalSystem.showMessage('Hello World');
 *   ModalSystem.questionYesNo('Delete?', 'Confirm', onYes, onNo);
 *   const id = ModalSystem.showWait('Loading...');
 *   ModalSystem.hide(id);
 *   ModalSystem.hide(); // closes topmost modal
 */

import { Logger } from './Logger';
import { ZIndex } from '../constants/ZIndex';

const logger = Logger.scope('ModalSystem');

export interface ModalButton {
  caption: string;
  callback?: () => void | boolean | Promise<void | boolean>;
  className?: string;
  primary?: boolean;
}

export interface ModalOptions {
  title?: string;
  message?: string;
  html?: string;
  buttons?: ModalButton[];
  preventClose?: boolean;
  width?: string;
  onOpened?: () => void;
  onClosed?: () => void;
}

/**
 * Modal manager for creating and managing modal dialogues.
 * Uses native <dialog> showModal()/close() for proper accessibility and behavior.
 */
export class ModalSystem {
  private static readonly MODAL_PREFIX = 'app-modal-';
  private static readonly MAX_STACK = 16;
  
  // Track open modals in a stack for proper ordering
  private static openModals: string[] = [];

  /**
   * Show a simple message with an OK button
   */
  public static showMessage(
    message: string,
    title?: string,
    onOK?: () => void
  ): string {
    return this.show({
      title: title || 'Message',
      message,
      buttons: [
        {
          caption: 'OK',
          callback: onOK,
          primary: true
        }
      ]
    });
  }

  /**
   * Show an error message
   */
  public static showError(
    error: string | Error,
    title?: string,
    onOK?: () => void
  ): string {
    const message = error instanceof Error ? error.message : error;
    return this.show({
      title: title || 'Error',
      html: `
        <div class="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>${message}</span>
        </div>
      `,
      buttons: [
        {
          caption: 'OK',
          callback: onOK,
          primary: true
        }
      ]
    });
  }

  /**
   * Show a Yes/No question
   */
  public static questionYesNo(
    message: string,
    title: string,
    onYes?: () => void,
    onNo?: () => void,
    yesCaption: string = 'Yes',
    noCaption: string = 'No'
  ): string {
    return this.show({
      title,
      message,
      buttons: [
        {
          caption: yesCaption,
          callback: onYes,
          primary: true,
          className: 'btn-success'
        },
        {
          caption: noCaption,
          callback: onNo,
          className: 'btn-error'
        }
      ]
    });
  }

  /**
   * Show a Yes/No/Cancel question
   */
  public static questionYesNoCancel(
    message: string,
    title: string,
    onYes?: () => void,
    onNo?: () => void,
    onCancel?: () => void,
    yesCaption: string = 'Yes',
    noCaption: string = 'No',
    cancelCaption: string = 'Cancel'
  ): string {
    return this.show({
      title,
      message,
      buttons: [
        {
          caption: yesCaption,
          callback: onYes,
          primary: true,
          className: 'btn-success'
        },
        {
          caption: noCaption,
          callback: onNo,
          className: 'btn-error'
        },
        {
          caption: cancelCaption,
          callback: onCancel
        }
      ]
    });
  }

  /**
   * Show a wait/loading message
   */
  public static showWait(
    message: string = 'Please wait...',
    title: string = 'Processing'
  ): string {
    return this.show({
      title,
      html: `
        <div class="flex flex-col items-center justify-center py-8">
          <span class="loading loading-spinner loading-lg text-primary"></span>
          <p class="mt-4 text-lg">${message}</p>
        </div>
      `,
      preventClose: true,
      buttons: []
    });
  }

  /**
   * Hide/close a modal.
   * @param modalId Optional modal ID. If not provided, closes the topmost modal.
   */
  public static hide(modalId?: string): void {
    const idToClose = modalId || this.openModals[this.openModals.length - 1];
    
    if (!idToClose) {
      logger.debug('No modal to close');
      return;
    }

    const modal = document.getElementById(idToClose) as HTMLElement & { _closeModal?: () => void };
    if (modal && modal._closeModal) {
      logger.debug('Closing modal:', idToClose);
      modal._closeModal();
    }
  }

  /**
   * Close all open modals
   */
  public static hideAll(): void {
    // Close from top to bottom
    while (this.openModals.length > 0) {
      this.hide();
    }
  }

  /**
   * Get the number of currently open modals
   */
  public static getOpenCount(): number {
    return this.openModals.length;
  }

  /**
   * Generic show method for custom modals.
   * Returns the modal ID which can be used with hide().
   */
  public static show(options: ModalOptions): string {
    if (this.openModals.length >= this.MAX_STACK) {
      logger.warn(`Cannot stack more than ${this.MAX_STACK} modals`);
      return '';
    }

    // Generate unique ID
    const modalId = `${this.MODAL_PREFIX}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const title = options.title || 'Message';
    const buttons = options.buttons || [];

    // Build content
    let content = '';
    if (options.html) {
      content = options.html;
    } else if (options.message) {
      content = `<p class="py-4">${options.message}</p>`;
    }

    // Determine width class or inline style for pixel values
    const widthMap: { [key: string]: string } = {
      'sm': 'max-w-sm',
      'md': 'max-w-md',
      'lg': 'max-w-lg',
      'xl': 'max-w-xl',
      '2xl': 'max-w-2xl',
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
      '7xl': 'max-w-7xl',
      'full': 'max-w-full'
    };
    
    // Check if width is a pixel/rem/em value (e.g., '1200px', '50rem')
    const isCustomWidth = options.width && /^\d+(\.\d+)?(px|rem|em|vw|%)$/.test(options.width);
    const widthClass = isCustomWidth ? 'max-w-none' : (options.width ? (widthMap[options.width] || `max-w-${options.width}`) : 'max-w-lg');
    const customWidthStyle = isCustomWidth ? `max-width: ${options.width}; width: ${options.width};` : '';

    // Build buttons HTML using DaisyUI theme-aware classes
    const buttonsHtml = buttons.map((btn: ModalButton, idx: number) => {
      const baseClasses = 'btn btn-md shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200';
      if (btn.primary) {
        // Use custom className if provided, otherwise default to btn-primary
        const btnClass = btn.className || 'btn-primary';
        return `<button class="${baseClasses} ${btnClass}" data-modal-btn="${idx}">${btn.caption}</button>`;
      } else {
        // Secondary button - use className if provided, otherwise btn-ghost
        const btnClass = btn.className || 'btn-ghost';
        return `<button class="${baseClasses} ${btnClass}" data-modal-btn="${idx}">${btn.caption}</button>`;
      }
    }).join('');

    // Create modal element - using div instead of dialog to stay in normal stacking context
    // This allows password manager dropdowns (Dashlane, 1Password, etc.) to appear above modal
    const modal = document.createElement('div');
    modal.id = modalId;
    modal.className = 'modal modal-open';
    // Ensure modal is fixed positioned for standalone pages using CDN daisyUI
    modal.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: ${ZIndex.MODAL + this.openModals.length};
      display: grid;
      place-items: center;
      overflow-y: auto;
    `;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    
    // Only apply backdrop blur on first modal to prevent compounding darkness
    const isFirstModal = this.openModals.length === 0;
    const backdropStyle = isFirstModal 
      ? 'position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(2px); z-index: -1;'
      : 'position: fixed; inset: 0; background: rgba(0,0,0,0.15); z-index: -1;';
    
    modal.innerHTML = `
      <div class="modal-backdrop" style="${backdropStyle}"></div>
      <div class="modal-box ${widthClass} w-11/12 flex flex-col max-h-[90vh] p-0 shadow-2xl overflow-hidden bg-base-100 rounded-2xl" style="${customWidthStyle}">
        <!-- Fixed Header -->
        <div class="sticky top-0 z-10 px-6 pt-5 pb-4 bg-base-100 border-b border-base-300 flex items-center">
          <h3 class="font-bold text-xl text-base-content flex-1">${title}</h3>
          ${!options.preventClose ? `<button class="btn btn-sm btn-ghost btn-circle ml-2" data-modal-close aria-label="Close"><i class="fas fa-times"></i></button>` : ''}
        </div>
        
        <!-- Scrollable Content -->
        <div class="modal-content overflow-y-auto flex-1 px-6 py-5 bg-base-100 text-base-content entity-form-scrollable">
          ${content}
        </div>
        
        <!-- Fixed Footer -->
        ${buttons.length > 0 ? `
          <div class="sticky bottom-0 z-10 px-6 pb-5 pt-4 bg-base-200 border-t border-base-300">
            <div class="flex flex-wrap gap-3 justify-end">
              ${buttonsHtml}
            </div>
          </div>
        ` : ''}
      </div>
    `;

    // Add to DOM
    document.body.appendChild(modal);

    // Helper function to close this modal
    const closeModal = () => {
      // Remove escape handler
      document.removeEventListener('keydown', handleEscape);
      
      modal.classList.remove('modal-open');
      
      // Remove from stack
      const idx = this.openModals.indexOf(modalId);
      if (idx > -1) {
        this.openModals.splice(idx, 1);
      }
      
      // Restore body scroll if no more modals
      if (this.openModals.length === 0) {
        document.body.style.overflow = '';
      }
      
      // Remove from DOM after animation
      setTimeout(() => {
        modal.remove();
      }, 300);
      
      // Call callback
      if (options.onClosed) {
        options.onClosed();
      }
      
      logger.debug('Modal closed:', modalId, 'Open modals:', this.openModals.length);
    };

    // Handle Escape key (defined before closeModal uses it)
    const handleEscape = (e: KeyboardEvent) => {
      // Only handle if this is the topmost modal
      if (this.openModals[this.openModals.length - 1] !== modalId) {
        return;
      }
      if (e.key === 'Escape') {
        if (options.preventClose) {
          e.preventDefault();
          e.stopPropagation();
        } else {
          closeModal();
        }
      }
    };

    // Attach button handlers
    buttons.forEach((btn, idx) => {
      const buttonEl = modal.querySelector(`[data-modal-btn="${idx}"]`);
      buttonEl?.addEventListener('click', async () => {
        if (btn.callback) {
          const result = btn.callback();
          // Handle both sync and async callbacks
          const shouldClose = result instanceof Promise ? await result : result;
          if (shouldClose !== false) {
            closeModal();
          }
        } else {
          closeModal();
        }
      });
    });

    // Attach header close button handler
    const closeBtn = modal.querySelector('[data-modal-close]');
    closeBtn?.addEventListener('click', () => { closeModal(); });

    // NOTE: We intentionally do NOT close on backdrop click
    // This is a UX decision: users should not accidentally lose their work
    // by clicking outside the modal. They must use explicit close actions
    // (buttons, Escape key if allowed, or programmatic close).

    document.addEventListener('keydown', handleEscape);
    
    // Store cleanup function on modal element for external hide() calls
    (modal as HTMLElement & { _closeModal?: () => void })._closeModal = closeModal;

    // Track this modal
    this.openModals.push(modalId);

    // Show modal by adding modal-open class (already added in className)
    // The modal is visible immediately since we used 'modal modal-open' class
    // Prevent body scroll while modal is open
    document.body.style.overflow = 'hidden';
    
    logger.debug('Modal opened:', modalId, 'Open modals:', this.openModals.length);

    // Password manager compatibility:
    // After modal is shown, we need to help password managers detect the new form.
    // Password managers use MutationObserver but may need a nudge for dialog elements.
    // We dispatch a 'DOMContentLoaded'-like signal by focusing the first input.
    setTimeout(() => {
      const firstInput = modal.querySelector('input:not([type="hidden"]):not([readonly])') as HTMLInputElement;
      if (firstInput) {
        // Focus triggers password manager field detection
        firstInput.focus();
      }
      
      // Call opened callback after password manager has had time to detect
      if (options.onOpened) {
        options.onOpened();
      }
    }, 100);

    return modalId;
  }
}

// Export for global access
declare global {
  interface Window {
    ModalSystem: typeof ModalSystem;
  }
}

if (typeof window !== 'undefined') {
  window.ModalSystem = ModalSystem;
}
